import React, { useContext } from 'react';
import { Sun, Moon, Lock, Bot } from 'lucide-react';
import { UserButton, OrganizationSwitcher } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { ThemeSwitchingContext } from '../App';
import { GraphIcon } from '../GraphList/GraphIcon';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarSeparator,
    useSidebar,
} from '@/components/ui/sidebar';

interface Props {
    onSecretsClick?: () => void;
}

export const AppSidebar: React.FC<Props> = ({ onSecretsClick }) => {
    const { toggleTheme, activeTheme } = useContext(ThemeSwitchingContext);
    const navigate = useNavigate();
    const { state } = useSidebar();
    const isCollapsed = state === 'collapsed';

    return (
        <Sidebar side="left" collapsible="icon">
            <SidebarHeader className="h-[53px] justify-center">
                <SidebarMenu>
                    <SidebarMenuItem>
                        <div className="flex items-center justify-center py-1 overflow-hidden">
                            <OrganizationSwitcher
                                afterSelectOrganizationUrl="/"
                                afterSelectPersonalUrl="/"
                                appearance={{
                                    elements: {
                                        rootBox: { width: '100%' },
                                        organizationSwitcherTrigger: {
                                            padding: isCollapsed ? 0 : undefined,
                                            width: '100%',
                                            justifyContent: isCollapsed ? 'center' : undefined,
                                            overflow: 'hidden',
                                        },
                                        organizationSwitcherTriggerIcon: isCollapsed ? { display: 'none' } : {},
                                        organizationPreview__organizationSwitcherTrigger: isCollapsed ? { gap: 0 } : {},
                                        organizationPreviewTextContainer__organizationSwitcherTrigger: isCollapsed
                                            ? { display: 'none' }
                                            : {},
                                        userPreviewTextContainer__organizationSwitcherTrigger: isCollapsed ? { display: 'none' } : {},
                                    },
                                }}
                            />
                        </div>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarSeparator />
            <SidebarContent>
                <SidebarGroup>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <SidebarMenuButton onClick={() => navigate('/')} tooltip="Workflows">
                                <GraphIcon size={16} color="currentColor" strokeWidth={12} />
                                <span>Workflows</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                            <SidebarMenuButton onClick={onSecretsClick} tooltip="Secrets">
                                <Lock className="h-4 w-4" />
                                <span>Secrets</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                        <SidebarMenuItem>
                            <SidebarMenuButton onClick={() => navigate('/agents')} tooltip="Agents">
                                <Bot className="h-4 w-4" />
                                <span>Agents</span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarGroup>
            </SidebarContent>
            <SidebarSeparator />
            <SidebarFooter>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton onClick={toggleTheme} tooltip={activeTheme === 'LIGHT' ? 'Dark Mode' : 'Light Mode'}>
                            {activeTheme === 'LIGHT' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                            <span>{activeTheme === 'LIGHT' ? 'Dark Mode' : 'Light Mode'}</span>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                        <div className="flex items-center justify-center gap-3 px-2 py-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
                            <UserButton />
                            <span className="text-sm text-sidebar-foreground group-data-[collapsible=icon]:hidden">Account</span>
                        </div>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
};
